import { useCallback, useEffect, useRef, useState } from "react";
import { saveAs } from "file-saver";

// Shared driver for every "export this as a PDF" button.
//
// Each of these screens had grown its own copy of the same promise chain, and
// every copy had the same two holes:
//
//   * no .catch() - a failed render left `loading` true forever, so the export
//     button stayed a spinner until the page was reloaded, with nothing on
//     screen to say anything had gone wrong;
//   * no feedback beyond a bare 28px spinner, while the server can genuinely
//     take ten or twenty seconds to rasterise a long report through PhantomJS.
//
// This runs the same POST-then-GET flow, but reports which phase it is in, can
// never strand the caller (finally always resolves the state), keeps a live
// elapsed counter so a slow export visibly stays alive, and exposes retry.

export const EXPORT_STAGES = ["building", "rendering", "downloading"];

const initialState = {
  status: "idle", // idle | running | success | error
  stage: null, // one of EXPORT_STAGES
  error: null,
  label: "",
  elapsed: 0,
};

export default function useReportExport() {
  const [state, setState] = useState(initialState);

  // The request outlives the component if the user navigates away mid-export,
  // so every setState goes through this guard.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const set = useCallback((patch) => {
    if (mountedRef.current) setState((prev) => ({ ...prev, ...patch }));
  }, []);

  // Held so Retry can re-run exactly the same export.
  const lastRunRef = useRef(null);
  const timerRef = useRef(null);

  const stopTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => stopTimer, [stopTimer]);

  const run = useCallback(
    async (config) => {
      const {
        label = "Report",
        create, // () => Promise  - asks the server to render the PDF
        fetch, // () => Promise<{data: Blob}>
        fileName = "report.pdf",
        openUrl, // optional: also open the PDF in a new tab
        onSuccess,
      } = config || {};

      lastRunRef.current = config;
      const startedAt = Date.now();

      stopTimer();
      set({ status: "running", stage: "building", error: null, label, elapsed: 0 });
      timerRef.current = setInterval(() => {
        if (!mountedRef.current) return stopTimer();
        set({ elapsed: Math.floor((Date.now() - startedAt) / 1000) });
      }, 1000);

      try {
        await create();
        set({ stage: "rendering" });

        const res = await fetch();
        set({ stage: "downloading" });

        const blob = new Blob([res.data], { type: "application/pdf" });
        // A zero-byte body means the server wrote no file - treat it as a
        // failure rather than handing the user an unopenable download.
        if (!blob.size) throw new Error("The server returned an empty PDF.");

        saveAs(blob, fileName);
        if (openUrl) window.open(openUrl, "_blank");

        set({ status: "success", stage: null });
        onSuccess?.();
      } catch (err) {
        set({
          status: "error",
          stage: null,
          error:
            err?.response?.data?.message ||
            err?.message ||
            "The report could not be generated.",
        });
      } finally {
        stopTimer();
      }
    },
    [set, stopTimer]
  );

  const retry = useCallback(() => {
    if (lastRunRef.current) run(lastRunRef.current);
  }, [run]);

  const reset = useCallback(() => {
    stopTimer();
    if (mountedRef.current) setState(initialState);
  }, [stopTimer]);

  return { ...state, run, retry, reset, isRunning: state.status === "running" };
}
